import React from 'react';
import {Skeleton} from 'antd';
import {ArrowUpOutlined, ArrowDownOutlined} from '@ant-design/icons';
import {useTranslation} from '../../../hooks/useTranslation';
import {LossEventStats} from '../../../types/events';

const formatMoney = (val?: number): string => {
  if (val == null) return '—';
  if (val >= 1_000_000) return `€${(val / 1_000_000).toFixed(2)}M`;
  if (val >= 1_000) return `€${Math.round(val / 1_000)}K`;
  return `€${val.toLocaleString('de-DE')}`;
};

const formatDays = (val?: number): string => {
  if (val == null) return '—';
  return `${Number.isInteger(val) ? val : val.toFixed(1)}d`;
};

interface KpiCardProps {
  label: string;
  value: string;
  trendUp?: boolean;
  trendText?: string;
  loading?: boolean;
}

const KpiCard: React.FC<KpiCardProps> = ({label, value, trendUp, trendText, loading}) => {
  const TrendIcon = trendUp ? ArrowUpOutlined : ArrowDownOutlined;
  const trendColor = trendUp ? '#65c376' : '#ff4d4f';
  const hasTrend = trendUp !== undefined;

  return (
    <div className="gt-events-kpi-card">
      <span className="gt-events-kpi-label">{label}</span>

      {loading ? (
        <Skeleton.Input active size="large" style={{width: 120, height: 36}} />
      ) : (
        <span className="gt-events-kpi-value">
          {hasTrend && (
            <TrendIcon style={{color: trendColor, fontSize: 18, marginRight: 6}} />
          )}
          {value}
        </span>
      )}

      {!loading && hasTrend && trendText && (
        <span className="gt-events-kpi-trend" style={{color: trendColor}}>
          <TrendIcon style={{fontSize: 11, marginRight: 3}} />
          {trendText}
        </span>
      )}
    </div>
  );
};

interface EventsKpiBarProps {
  stats?: LossEventStats;
  loading?: boolean;
}

const EventsKpiBar: React.FC<EventsKpiBarProps> = ({stats, loading}) => {
  const {t} = useTranslation();

  const openTrendText = stats?.openCountDelta != null
    ? `${stats.openCountDelta} ${t('events.stats.trendVsLastWeek')}`
    : undefined;

  const grossTrendText = stats?.grossLossYtdDelta != null
    ? `${formatMoney(stats.grossLossYtdDelta)} ${t('events.stats.trendThisMonth')}`
    : undefined;

  const netTrendText = stats?.netLossYtdRecoveredPct != null
    ? `${stats.netLossYtdRecoveredPct}% ${t('events.stats.trendRecovered')}`
    : undefined;

  const avgTrendText = stats?.avgDaysToCloseDelta != null
    ? `${formatDays(stats.avgDaysToCloseDelta)} ${t('events.stats.trendVsLastQuarter')}`
    : undefined;

  return (
    <div className="gt-events-kpi-bar">
      <KpiCard
        label={t('events.stats.openEvents')}
        value={stats?.openCount != null ? String(stats.openCount) : '—'}
        trendUp={stats?.openCountTrendUp}
        trendText={openTrendText}
        loading={loading}
      />
      <KpiCard
        label={t('events.stats.grossLossYtd')}
        value={formatMoney(stats?.grossLossYtd)}
        trendUp={stats?.grossLossYtdTrendUp}
        trendText={grossTrendText}
        loading={loading}
      />
      <KpiCard
        label={t('events.stats.netLossYtd')}
        value={formatMoney(stats?.netLossYtd)}
        trendUp={stats?.netLossYtdTrendUp}
        trendText={netTrendText}
        loading={loading}
      />
      <KpiCard
        label={t('events.stats.avgTimeToClose')}
        value={formatDays(stats?.avgDaysToClose)}
        trendUp={stats?.avgDaysToCloseTrendUp}
        trendText={avgTrendText}
        loading={loading}
      />
    </div>
  );
};

export default EventsKpiBar;
